/**
 * Storage Permission Checker
 *
 * This service handles application-level storage permission checks.
 * It maps storage paths to permission categories and checks if a user's role
 * has the appropriate read/write access.
 *
 * Note: This provides application-level security. Firebase Storage rules
 * still provide server-side enforcement, but some paths use auth-only rules
 * for reliability (due to dual-ID system issues).
 */

import { UserRole } from '../../types/user.types';
import { StoragePermissions, StoragePathCategory } from '../../types/permission.types';
import { getRoleStoragePermissions } from '../permissions/permissionService';

// Map storage paths to permission categories
const PATH_MAPPING: Record<string, StoragePathCategory> = {
  '/developments/': 'developments',
  '/deals/': 'deals',
  '/reservations/': 'reservations',
  '/consultantLoans/': 'consultantLoans',
  '/documents/': 'documents',
  '/invoices/': 'invoices',
  '/marketingAssets/': 'marketingAssets',
  '/emailTemplates/': 'emailTemplates',
  '/users/': 'userPhotos',
  '/viewings/': 'viewings',
  '/templates/': 'templates',
};

/**
 * Get the permission category for a storage path
 */
export const getPathCategory = (fullPath: string): StoragePathCategory | null => {
  // Normalize path - ensure it starts with /
  const normalizedPath = fullPath.startsWith('/') ? fullPath : `/${fullPath}`;

  for (const [prefix, category] of Object.entries(PATH_MAPPING)) {
    if (normalizedPath.startsWith(prefix)) {
      return category;
    }
  }

  return null;
};

/**
 * Check if a role can write to a storage path
 */
export const canWriteToPath = async (
  userRole: UserRole,
  storagePath: string,
  storagePermissions?: StoragePermissions | null
): Promise<boolean> => {
  // Super users always have access
  if (userRole === UserRole.PROPCLOUDAI_SUPER_USER) {
    return true;
  }

  const category = getPathCategory(storagePath);
  if (!category) {
    // Unknown path - log warning and allow for flexibility
    console.warn(`[StoragePermissionChecker] Unknown storage path category: ${storagePath}`);
    return true;
  }

  // Get storage permissions from parameter or fetch from service
  const perms = storagePermissions || (await getRoleStoragePermissions(userRole));

  if (!perms) {
    // No permissions configured - default to deny
    console.warn(`[StoragePermissionChecker] No storage permissions for role: ${userRole}`);
    return false;
  }

  return perms[category]?.write ?? false;
};

/**
 * Check if a role can read from a storage path
 */
export const canReadFromPath = async (
  userRole: UserRole,
  storagePath: string,
  storagePermissions?: StoragePermissions | null
): Promise<boolean> => {
  // Super users always have access
  if (userRole === UserRole.PROPCLOUDAI_SUPER_USER) {
    return true;
  }

  const category = getPathCategory(storagePath);
  if (!category) {
    // Unknown path - log warning and allow for flexibility
    console.warn(`[StoragePermissionChecker] Unknown storage path category: ${storagePath}`);
    return true;
  }

  // Get storage permissions from parameter or fetch from service
  const perms = storagePermissions || (await getRoleStoragePermissions(userRole));

  if (!perms) {
    // No permissions configured - default to deny
    console.warn(`[StoragePermissionChecker] No storage permissions for role: ${userRole}`);
    return false;
  }

  return perms[category]?.read ?? false;
};

/**
 * Synchronous check for write permission (uses cached/provided permissions)
 */
export const canWriteToPathSync = (
  userRole: UserRole,
  storagePath: string,
  storagePermissions: StoragePermissions | null
): boolean => {
  // Super users always have access
  if (userRole === UserRole.PROPCLOUDAI_SUPER_USER) {
    return true;
  }

  if (!storagePermissions) {
    return false;
  }

  const category = getPathCategory(storagePath);
  if (!category) {
    // Unknown path - allow for flexibility
    return true;
  }

  return storagePermissions[category]?.write ?? false;
};

/**
 * Synchronous check for read permission (uses cached/provided permissions)
 */
export const canReadFromPathSync = (
  userRole: UserRole,
  storagePath: string,
  storagePermissions: StoragePermissions | null
): boolean => {
  // Super users always have access
  if (userRole === UserRole.PROPCLOUDAI_SUPER_USER) {
    return true;
  }

  if (!storagePermissions) {
    return false;
  }

  const category = getPathCategory(storagePath);
  if (!category) {
    // Unknown path - allow for flexibility
    return true;
  }

  return storagePermissions[category]?.read ?? false;
};

/**
 * Get all categories and their paths
 */
export const getAllPathCategories = (): { category: StoragePathCategory; pathPrefix: string }[] => {
  return Object.entries(PATH_MAPPING).map(([pathPrefix, category]) => ({
    pathPrefix,
    category,
  }));
};

/**
 * Custom error class for storage permission denials
 */
export class StoragePermissionError extends Error {
  public readonly path: string;
  public readonly category: StoragePathCategory | null;
  public readonly action: 'read' | 'write';
  public readonly role: UserRole;

  constructor(
    path: string,
    action: 'read' | 'write',
    role: UserRole,
    category: StoragePathCategory | null
  ) {
    super(`Storage ${action} permission denied for path: ${path}`);
    this.name = 'StoragePermissionError';
    this.path = path;
    this.action = action;
    this.role = role;
    this.category = category;
  }
}
